import React from 'react';
import { AlertCircle, X } from 'lucide-react';
import { EcointelLottieMark } from '../EcoIntelLoader';

/**
 * Confirmation modal for deleting a single parcel document (admin only).
 * Extracted from AdminDashboard.js so PropertiesAdmin can mount it without
 * pulling in the entire admin monolith.
 */
export default function ParcelDeleteConfirmModal({ farmId, farmLabel, parcel, onClose, onConfirm, deleting }) {
  if (!parcel) return null;
  const display = parcel.displayName || parcel.name || parcel.parcel_id || parcel.docId;
  const isWholeSite =
    parcel.analyticsParcelName === 'WHOLE_SITE' ||
    parcel.name === 'WHOLE_SITE' ||
    (parcel.parcel_id || '').toString().trim() === 'WHOLE_SITE';

  const handleBackdrop = () => {
    if (!deleting) onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] backdrop-blur-sm" onClick={handleBackdrop}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-lg text-slate-800">Delete parcel?</h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600" disabled={deleting}>
            <X size={18} />
          </button>
        </div>
        {isWholeSite ? (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
            This is a whole-site analytics parcel. It cannot be removed from here.
          </div>
        ) : (
          <>
            <p className="text-sm text-slate-600 mb-3">
              You are about to remove the parcel document for{' '}
              <span className="font-semibold text-slate-800">{display}</span>{' '}
              from property <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">{farmLabel || farmId}</span>.
            </p>
            <div className="mb-4 p-3 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-600 space-y-1">
              <div><span className="text-slate-400">Firestore doc:</span> <span className="font-mono">{parcel.docId}</span></div>
              {parcel.parcel_id && parcel.parcel_id !== parcel.docId && (
                <div><span className="text-slate-400">parcel_id:</span> <span className="font-mono">{parcel.parcel_id}</span></div>
              )}
            </div>
            <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-lg text-sm text-red-800 flex items-start gap-2">
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              <span>
                This cannot be undone. Cached analytics for this property will be cleared. Earth Engine assets are not deleted. You can re-import or re-sync the parcel from config if needed.
              </span>
            </div>
          </>
        )}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={deleting}
            className="px-4 py-2 text-slate-500 hover:text-slate-700 text-sm font-medium disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={deleting || isWholeSite}
            className="px-5 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium text-sm transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {deleting ? <><EcointelLottieMark size={14} /> Removing...</> : 'Delete parcel'}
          </button>
        </div>
      </div>
    </div>
  );
}
